<template>
  <div ref="dropdownRef" class="relative">
    <!-- Trigger Button -->
    <button
      type="button"
      class="w-full group"
      :class="disabled && 'cursor-not-allowed opacity-60'"
      :disabled="disabled"
      :aria-disabled="disabled"
      @click="toggleOpen"
    >
      <div
        class="w-full rounded-xl border bg-white px-3 py-3 text-left shadow-sm transition-all duration-200"
        :class="[
          isOpen
            ? 'border-[#1980e6] ring-2 ring-[#1980e6]/20'
            : disabled
              ? 'border-gray-300 bg-gray-100'
              : 'border-gray-300 hover:border-gray-400',
        ]"
      >
        <div class="flex items-center gap-3">
          <!-- Token Icon -->
          <div class="relative flex-shrink-0">
            <TokenIcon
              v-if="selectedToken"
              :symbol="selectedToken.symbol"
              :address="selectedToken.address"
              :logo-u-r-i="selectedToken.logoURI"
              :size="32"
            />
            <div
              v-else
              class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          <!-- Token Info -->
          <div class="flex-1 min-w-0">
            <div v-if="selectedToken" class="flex items-baseline gap-2">
              <span class="font-semibold text-[#0e141b] text-base">{{ selectedToken.symbol }}</span>
              <svg
                v-if="isLoading"
                class="w-3.5 h-3.5 animate-spin text-[#1980e6] self-center"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span v-if="isEvmBalanceLoading" class="text-xs text-gray-400">
                Loading balance...
              </span>
              <span v-else-if="selectedToken.hasBalance" class="text-sm text-gray-400">
                {{ formatBalance(selectedToken.balanceFormatted) }}
              </span>
              <span
                v-else-if="selectedToken.balanceStatus === 'unavailable'"
                class="text-xs text-amber-600"
              >
                Balance unavailable
              </span>
            </div>
            <div v-else class="text-gray-400">
              {{ showBlockingLoader ? 'Loading tokens...' : 'Select token' }}
            </div>
          </div>

          <!-- Chevron -->
          <svg
            class="w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0"
            :class="isOpen && 'rotate-180'"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="isOpen"
        class="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden"
      >
        <!-- Search & Filter Header -->
        <div class="p-3 border-b border-gray-100 space-y-3">
          <!-- Search Input -->
          <div class="relative">
            <svg
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or paste address"
              class="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-[#0e141b] placeholder-gray-400 focus:outline-none focus:border-[#1980e6] focus:ring-1 focus:ring-[#1980e6]/20 transition-all"
              @click.stop
            />
            <button
              v-if="searchQuery"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-[#0e141b]"
              @click.stop="searchQuery = ''"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Filter Toggle (hidden for Solana — shows only swappable tokens) -->
          <div v-if="!isSolanaChain" class="flex items-center gap-2">
            <button
              type="button"
              class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              :class="
                showOnlyWithBalance
                  ? 'bg-[#1980e6] text-white'
                  : 'bg-gray-100 text-gray-500 hover:text-[#0e141b]'
              "
              @click.stop="showOnlyWithBalance = true"
            >
              My tokens
              <span v-if="tokensWithBalanceCount > 0" class="ml-1 opacity-80">
                ({{ tokensWithBalanceCount }})
              </span>
            </button>
            <button
              type="button"
              class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              :class="
                !showOnlyWithBalance
                  ? 'bg-[#1980e6] text-white'
                  : 'bg-gray-100 text-gray-500 hover:text-[#0e141b]'
              "
              @click.stop="showOnlyWithBalance = false"
            >
              All tokens
            </button>
          </div>
        </div>

        <!-- Token List with Virtual Scroll -->
        <div class="overflow-y-auto" style="max-height: 320px">
          <!-- Loading State (covers initial load AND mid-fetch when cached symbols
               have not yet been hydrated with on-chain balances — without this
               the empty-state branch flashes "No tokens with balance" while the
               EVM multicall is still in flight). -->
          <div v-if="showBlockingLoader" class="p-8 text-center">
            <div
              class="w-8 h-8 border-2 border-[#1980e6] border-t-transparent rounded-full animate-spin mx-auto mb-3"
            />

            <p class="text-sm text-gray-400">{{ loadingMessage }}</p>
          </div>

          <!-- Empty States -->
          <div v-else-if="filteredTokens.length === 0" class="p-8 text-center">
            <div v-if="searchQuery" class="space-y-2">
              <svg
                class="w-10 h-10 text-gray-400 mx-auto mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p class="text-sm text-[#0e141b]">No tokens found</p>
              <p class="text-xs text-gray-400">Try a different search term</p>
            </div>
            <div v-else-if="showOnlyWithBalance && props.tokens.length > 0" class="space-y-3">
              <svg
                class="w-10 h-10 text-gray-400 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p class="text-sm text-[#0e141b]">No tokens with balance</p>
              <button
                type="button"
                class="text-xs text-[#1980e6] hover:text-[#4aa3ff] underline"
                @click.stop="showOnlyWithBalance = false"
              >
                Show all tokens
              </button>
            </div>
            <div v-else class="space-y-2">
              <svg
                class="w-10 h-10 text-gray-400 mx-auto mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p class="text-sm text-[#0e141b]">No tokens available</p>
            </div>
          </div>

          <!-- Token List -->
          <template v-else>
            <div class="overflow-y-auto" style="max-height: 320px">
              <button
                v-for="token in filteredTokens"
                :key="token.address"
                type="button"
                class="w-full px-3 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                :class="isSelected(token) && 'bg-blue-50'"
                @click="selectToken(token)"
              >
                <TokenIcon
                  :symbol="token.symbol"
                  :address="token.address"
                  :logo-u-r-i="token.logoURI"
                  :size="36"
                />
                <div class="flex-1 min-w-0 text-left">
                  <div class="flex items-baseline gap-2">
                    <span class="font-semibold text-[#0e141b]">{{ token.symbol }}</span>
                    <span v-if="token.hasBalance" class="text-sm text-gray-400">
                      {{ formatBalance(token.balanceFormatted) }}
                    </span>
                    <span
                      v-else-if="token.balanceStatus === 'unavailable'"
                      class="text-xs text-amber-600"
                    >
                      Balance unavailable
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 truncate">
                    {{ token.name }}
                  </p>
                </div>
                <div v-if="isSelected(token)" class="text-[#1980e6]">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, toRef, watch, nextTick } from 'vue'
import TokenIcon from './TokenIcon.vue'
import type { WalletSourceToken } from './contracts'
import { useTokenFilter } from './use-token-filter'

interface Props {
  tokens: WalletSourceToken[]
  selectedAddress?: string
  isLoading?: boolean
  hasConnectedWallet?: boolean
  chainId?: number
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  tokens: () => [],
  selectedAddress: '',
  isLoading: false,
  hasConnectedWallet: false,
  chainId: undefined,
  disabled: false,
})

const emit = defineEmits<{
  select: [token: WalletSourceToken]
}>()

const isOpen = ref(false)
const toggleOpen = () => {
  if (!props.disabled) isOpen.value = !isOpen.value
}
const dropdownRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)

const {
  isSolanaChain,
  searchQuery,
  showOnlyWithBalance,
  selectedToken,
  tokensWithBalanceCount,
  filteredTokens,
  isSelected,
  selectToken: selectTokenFromFilter,
  formatBalance,
} = useTokenFilter({
  tokens: toRef(props, 'tokens'),
  selectedAddress: toRef(props, 'selectedAddress'),
  hasConnectedWallet: toRef(props, 'hasConnectedWallet'),
  chainId: toRef(props, 'chainId'),
  emit: (token) => {
    emit('select', token)
  },
})

const isEvmBalanceLoading = computed(() => props.isLoading && !isSolanaChain.value)

const showBlockingLoader = computed(() => {
  if (!props.isLoading) return false
  if (isEvmBalanceLoading.value) return true
  if (props.tokens.length === 0) return true
  return showOnlyWithBalance.value && !searchQuery.value.trim() && filteredTokens.value.length === 0
})

const loadingMessage = computed(() =>
  isEvmBalanceLoading.value && props.tokens.length > 0 ? 'Loading balances...' : 'Loading tokens...'
)

const selectToken = (token: WalletSourceToken) => {
  if (props.disabled) return
  selectTokenFromFilter(token)
  isOpen.value = false
}

// Reset scroll when dropdown opens or filters change
watch([isOpen, searchQuery, showOnlyWithBalance], () => {
  const container = dropdownRef.value?.querySelector('.overflow-y-auto')
  if (container) {
    container.scrollTop = 0
  }
})

// Focus search input when dropdown opens
watch(isOpen, async (open) => {
  if (open) {
    await nextTick()
    searchInputRef.value?.focus()
  }
})

// Close on click outside
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) isOpen.value = false
  }
)

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
